import React from 'react';
import {
  AgnesButton,
  AgnesDataTable,
  type AgnesDataTableColumn,
  AgnesDatePickerInput,
  AgnesIconButton,
  AgnesInput,
  AgnesInputFile,
  AgnesLabel,
  AgnesModal,
  AgnesPageHeader,
  AgnesPopover,
  AgnesPopoverContent,
  AgnesPopoverTrigger,
  AgnesSelect,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesTooltip,
} from '@agnes/ui';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import {
  CircleAlert,
  Download,
  FileText,
  Filter,
  History,
  Info,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import { ExportButton } from '@/components/ExportButton';
import { useCrfStore } from '@/stores/useCrfStore';
import { confirm } from '@/stores/useConfirm';
import { useEmpregadoresStore } from '@/stores/useEmpregadoresStore';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import { countFiltros, labelFiltro } from '@/lib/filtros';
import { SearchInput } from '@/components/SearchInput';
import UtilsMask from '@/utils/UtilsMask';
import ServiceCrf from '@/services/ServiceCrf';
import { notify } from '@/stores/useToast';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

const SELECT_TRIGGER =
  '[&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:flex-1 [&>[data-slot=select-value]]:truncate [&>[data-slot=select-value]]:text-left';

type Any = any;

// baixa o recibo do CRF via URL presigned (erro não estoura — notify)
async function baixarCrf(key: string) {
  try {
    const { data } = await ServiceCrf.downloadUrl(key);
    if (!data?.url) {
      notify.error('Documento indisponível.');
      return;
    }
    window.open(data.url, '_blank');
  } catch {
    notify.error('Não foi possível baixar o documento.');
  }
}

interface Crf {
  [k: string]: unknown;
  id: number;
  empresa: string;
  cnpj: string;
  emissao: string;
  validade: string;
  codigo: string;
  situacao: string;
  tipoInclusao: string;
  arquivoS3Id: string;
}

// valores de `status` = enum do backend (Crf.status), label PT-BR na UI.
const SITUACOES = [
  { value: 'REGULAR', label: 'Regular' },
  { value: 'IRREGULAR', label: 'Irregular' },
];

// modal Adicionar/Editar certidão (CRF) — mirror do CRUD manual da Certidão Federal.
function AdicionarCrf({
  open,
  onClose,
  editar,
}: {
  open: boolean;
  onClose: () => void;
  editar?: Any;
}) {
  const empregadores = useEmpregadoresStore((s) => s.lista);
  const opcoesEmpregador = empregadores.map((e) => ({
    value: e.cnpjFilial,
    label: e.company || e.fantasyName || '',
  }));
  const [f, setF] = React.useState({
    empregador: '',
    situacao: '',
    codigo: '',
    emissao: '',
    validade: '',
    arquivoKey: '',
  });
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const irregular = f.situacao === 'IRREGULAR';
  const podeSalvar =
    f.empregador !== '' &&
    f.situacao !== '' &&
    (irregular || (f.codigo !== '' && f.emissao !== '' && f.validade !== ''));

  React.useEffect(() => {
    if (!open) return;
    if (editar) {
      setF({
        empregador: editar.cnpj ?? '',
        situacao: editar._status ?? 'REGULAR',
        codigo: editar.codigo ?? '',
        emissao: editar._emissao ?? '',
        validade: editar._validade ?? '',
        arquivoKey: editar.arquivoS3Id ?? '',
      });
    } else {
      setF({ empregador: '', situacao: '', codigo: '', emissao: '', validade: '', arquivoKey: '' });
    }
  }, [editar, open]);

  return (
    <AgnesModal
      open={open}
      title={editar ? 'Editar certidão' : 'Adicionar certidão'}
      maxWidth="md"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-3">
          <AgnesButton variant="text" onClick={onClose}>
            Cancelar
          </AgnesButton>
          <AgnesButton
            variant="primary"
            disabled={!podeSalvar}
            onClick={async () => {
              const dto = {
                cnpj: f.empregador,
                status: f.situacao,
                codigo: f.codigo || undefined,
                dataEmissao: f.emissao || undefined,
                dataValidade: f.validade || undefined,
                arquivoS3Id: f.arquivoKey || undefined,
              };
              if (editar?.id) await useCrfStore.getState().atualizar(editar.id, dto);
              else await useCrfStore.getState().salvar(dto);
              onClose();
            }}
          >
            Salvar
          </AgnesButton>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <AgnesLabel required>Empregador</AgnesLabel>
          <AgnesSelect value={f.empregador} onValueChange={set('empregador')}>
            <AgnesSelectTrigger className={SELECT_TRIGGER}>
              <AgnesSelectValue placeholder="Selecione uma opção" />
            </AgnesSelectTrigger>
            <AgnesSelectContent>
              {opcoesEmpregador.map((o) => (
                <AgnesSelectItem key={o.value} value={o.value}>
                  {o.label}
                </AgnesSelectItem>
              ))}
            </AgnesSelectContent>
          </AgnesSelect>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col items-start gap-1.5">
            <AgnesLabel required>Situação</AgnesLabel>
            <AgnesSelect value={f.situacao} onValueChange={set('situacao')}>
              <AgnesSelectTrigger className={SELECT_TRIGGER}>
                <AgnesSelectValue placeholder="Selecione" />
              </AgnesSelectTrigger>
              <AgnesSelectContent>
                {SITUACOES.map((o) => (
                  <AgnesSelectItem key={o.value} value={o.value}>
                    {o.label}
                  </AgnesSelectItem>
                ))}
              </AgnesSelectContent>
            </AgnesSelect>
          </div>

          <AgnesInput
            required
            label="Código"
            hint="Preencha 22 caracteres (A-z, 0-9)"
            value={f.codigo}
            onChange={(e) => set('codigo')(e.target.value.toUpperCase())}
            placeholder="Obrigatório"
            disabled={irregular}
            maxLength={22}
          />

          <AgnesDatePickerInput
            required
            label="Emissão"
            hint="Preencha a data atual ou retroativa"
            value={f.emissao}
            onChange={(value) => set('emissao')(value)}
            placeholder="Obrigatório"
            disabled={irregular}
          />

          <AgnesDatePickerInput
            required
            label="Validade"
            hint="Preencha uma data futura"
            value={f.validade}
            onChange={(value) => set('validade')(value)}
            placeholder="Obrigatório"
            disabled={irregular}
          />
        </div>

        <div>
          <p className="mb-2 text-sm text-agnes-text-muted">
            Tamanho máximo do arquivo é 10MB. O tipo de arquivo suportado é .pdf
          </p>
          <AgnesInputFile
            accept="application/pdf"
            disabled={irregular}
            buttonLabel="Adicionar arquivo *"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const cnpjLimpo = (f.empregador || '').replace(/[^a-zA-Z0-9]/g, '');
              const key = `${cnpjLimpo.slice(0, 8)}/certidoes/crf/manual_${cnpjLimpo}_${Date.now()}.pdf`;
              try {
                await ServiceCrf.uploadArquivo(file, key);
                setF((s) => ({ ...s, arquivoKey: key }));
              } catch {
                notify.error('Falha no upload do arquivo.');
              }
            }}
          />
          {f.arquivoKey && (
            <div
              className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-agnes-border px-3 py-2"
              data-qa="crf-arquivo-anexado"
            >
              <span className="truncate text-sm text-agnes-text">Documento anexado</span>
              <AgnesButton
                variant="text"
                size="sm"
                startIcon={<Download className="h-4 w-4" />}
                onClick={() => baixarCrf(f.arquivoKey)}
                aria-label="Baixar documento anexado"
                data-qa="crf-arquivo-anexado-baixar"
              >
                Baixar
              </AgnesButton>
            </div>
          )}
        </div>

        <p className="text-sm font-semibold text-agnes-text">
          * Campos de preenchimento obrigatório
        </p>
      </div>
    </AgnesModal>
  );
}

// popover Filtro — aplica empregador + situação server-side (o BFF /search só filtra por
// esses dois; range de data / tipo de inclusão não são suportados e foram removidos do form).
function CrfFiltro() {
  const empregadores = useEmpregadoresStore((s) => s.lista);
  const opcoesEmpregador = empregadores.map((e) => ({
    value: e.cnpjFilial,
    label: e.company || e.fantasyName || '',
  }));
  const [open, setOpen] = React.useState(false);
  const [f, setF] = React.useState({ empregador: '', status: '' });
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const filtroAplicado = useCrfStore((s) => s.filtro);
  const nFiltros = countFiltros(filtroAplicado);
  const aplicar = () => {
    useCrfStore.getState().aplicarFiltro(f);
    setOpen(false);
  };
  const limpar = () => {
    setF({ empregador: '', status: '' });
    useCrfStore.getState().limparFiltro();
  };
  return (
    <AgnesPopover open={open} onOpenChange={setOpen}>
      <AgnesPopoverTrigger asChild>
        <AgnesButton
          variant="outlined"
          startIcon={<Filter className="h-4 w-4" />}
          aria-label={nFiltros > 0 ? `Filtro, ${nFiltros} ativos` : 'Filtro'}
        >
          {labelFiltro('Filtro', nFiltros)}
        </AgnesButton>
      </AgnesPopoverTrigger>
      <AgnesPopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-agnes-border px-4 py-3">
          <span className="text-base font-semibold text-agnes-text">Filtro</span>
          <button
            type="button"
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="text-agnes-text-muted hover:text-agnes-text"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-col gap-3 px-4 py-4">
          <AgnesSelect value={f.empregador} onValueChange={set('empregador')}>
            <AgnesSelectTrigger className={SELECT_TRIGGER}>
              <AgnesSelectValue placeholder="Empregador" />
            </AgnesSelectTrigger>
            <AgnesSelectContent>
              {opcoesEmpregador.map((o) => (
                <AgnesSelectItem key={o.value} value={o.value}>
                  {o.label}
                </AgnesSelectItem>
              ))}
            </AgnesSelectContent>
          </AgnesSelect>
          <AgnesSelect value={f.status} onValueChange={set('status')}>
            <AgnesSelectTrigger className={SELECT_TRIGGER}>
              <AgnesSelectValue placeholder="Situação" />
            </AgnesSelectTrigger>
            <AgnesSelectContent>
              {SITUACOES.map((o) => (
                <AgnesSelectItem key={o.value} value={o.value}>
                  {o.label}
                </AgnesSelectItem>
              ))}
            </AgnesSelectContent>
          </AgnesSelect>
        </div>
        <div className="flex flex-col gap-2 border-t border-agnes-border px-4 py-3">
          <AgnesButton variant="primary" onClick={aplicar} className="w-full">
            Aplicar filtro
          </AgnesButton>
          <AgnesButton
            variant="outlined"
            onClick={limpar}
            startIcon={<Trash2 className="h-4 w-4" />}
            className="w-full"
          >
            Limpar filtro
          </AgnesButton>
        </div>
      </AgnesPopoverContent>
    </AgnesPopover>
  );
}

const CNPJS_MONITORADOS_INFO =
  'As certidões listadas nesta tela são centralizadas no CNPJ matriz, logo, a certidão considera todos os CNPJs que possuam o mesmo CNPJ raiz. Deste modo a certidão abarca matriz e todas as filiais que compartilham o mesmo CNPJ raiz (8 primeiros dígitos do CNPJ são os mesmos).';

function StatCard({
  title,
  value,
  caption,
  accent,
}: {
  title: React.ReactNode;
  value: number;
  caption: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl border border-agnes-border bg-agnes-bg-surface p-5 ${accent ? 'border-t-4 border-t-agnes-success' : ''}`}
    >
      <div className="flex items-start justify-between">
        <p className="text-agnes-text">{title}</p>
        {accent && (
          <AgnesTooltip className="max-w-sm whitespace-normal" content={CNPJS_MONITORADOS_INFO}>
            <Info className="h-4 w-4 shrink-0 text-agnes-primary" />
          </AgnesTooltip>
        )}
      </div>
      <p className="my-2 text-4xl font-bold text-agnes-text">{value}</p>
      <p className="text-sm text-agnes-text-muted">{caption}</p>
    </div>
  );
}

export default function PageCrf() {
  const [modal, setModal] = React.useState(false);
  const [editarLinha, setEditarLinha] = React.useState<Any | null>(null);
  const [histId, setHistId] = React.useState<string | null>(null);
  const [syncCnpj, setSyncCnpj] = React.useState('');

  const lista = useCrfStore((s) => s.lista);
  const total = useCrfStore((s) => s.total);
  const page = useCrfStore((s) => s.page);
  const size = useCrfStore((s) => s.size);
  const busca = useCrfStore((s) => s.busca);
  const setBusca = useCrfStore((s) => s.setBusca);
  const sortBy = useCrfStore((s) => s.sortBy);
  const sortOrder = useCrfStore((s) => s.sortOrder);
  const setSort = useCrfStore((s) => s.setSort);
  const dash = useCrfStore((s) => s.dashboard);
  const carregando = useCrfStore((s) => s.carregando);
  const historico = useCrfStore((s) => s.historico);
  const sincronizando = useCrfStore((s) => s.sincronizando);
  const exportando = useCrfStore((s) => s.exportando);
  const empregadores = useEmpregadoresStore((s) => s.lista);
  const { hiddenColumnKeys, toggleColumn } = useColumnPreferences('certidoes-crf');

  React.useEffect(() => {
    useEmpregadoresStore.getState().buscar();
    useCrfStore.getState().carregar();
    useCrfStore.getState().carregarDashboard();
  }, []);

  const columns: AgnesDataTableColumn<Crf>[] = [
    {
      key: 'idx',
      header: '',
      width: 48,
      toggleable: false,
      render: (r) => (
        <span className="text-agnes-text-muted">{lista.indexOf(r) + 1 + page * size}</span>
      ),
    },
    {
      key: 'empresa',
      header: 'Empregador',
      width: 280,
      sortable: true,
      render: (r) =>
        r.empresa ? (
          <span>{r.empresa}</span>
        ) : (
          <span className="flex items-center gap-1.5 text-agnes-error">
            <CircleAlert className="h-4 w-4 shrink-0" /> Campo sem preenchimento
          </span>
        ),
    },
    {
      key: 'cnpj',
      header: 'CNPJ',
      width: 170,
      sortable: true,
      render: (r) => UtilsMask.cnpj(r.cnpj),
    },
    { key: 'emissao', header: 'Emissão', width: 130, sortable: true },
    { key: 'validade', header: 'Validade', width: 130, sortable: true },
    { key: 'codigo', header: 'Código de controle', width: 220, sortable: true },
    {
      key: 'situacao',
      header: 'Situação',
      width: 120,
      sortable: true,
      render: (r) => (
        <span className={r.situacao === 'Regular' ? 'text-agnes-success' : 'text-agnes-error'}>
          {r.situacao}
        </span>
      ),
    },
    {
      key: 'acoes',
      header: 'Ações',
      width: 170,
      align: 'center',
      toggleable: false,
      render: (r) => (
        <div className="flex items-center justify-center gap-1">
          <AgnesIconButton
            aria-label="Download"
            size="sm"
            disabled={!r.arquivoS3Id}
            onClick={() => baixarCrf(r.arquivoS3Id)}
          >
            <Download className="h-4 w-4" />
          </AgnesIconButton>
          <AgnesIconButton
            aria-label="Histórico"
            size="sm"
            onClick={() => {
              setHistId(String(r.id));
              useCrfStore.getState().carregarHistorico(String(r.id));
            }}
          >
            <History className="h-4 w-4" />
          </AgnesIconButton>
          <AgnesIconButton
            aria-label="Editar"
            size="sm"
            onClick={() => {
              setEditarLinha(r);
              setModal(true);
            }}
          >
            <Pencil className="h-4 w-4" />
          </AgnesIconButton>
          <AgnesIconButton
            aria-label="Excluir"
            data-qa="crf-acao-excluir"
            size="sm"
            onClick={async () => {
              const ok = await confirm({
                title: 'Excluir CRF',
                message: 'Deseja excluir este CRF?',
                okLabel: 'Excluir',
                cancelLabel: 'Cancelar',
                dataQa: 'crf-excluir-modal',
              });
              if (ok) useCrfStore.getState().excluir(String(r.id));
            }}
          >
            <Trash2 className="h-4 w-4" />
          </AgnesIconButton>
        </div>
      ),
    },
  ];

  return (
    <>
      <AgnesPageHeader
        icon={<FileText />}
        title="Certificado de Regularidade do FGTS – CRF"
        subtitle="Monitore a regularidade fundiária"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Certidões' },
          { label: 'Certificado de Regularidade do FGTS – CRF' },
        ]}
        actions={
          <AgnesButton
            variant="primary"
            startIcon={<Plus className="h-4 w-4" />}
            onClick={() => {
              setEditarLinha(null);
              setModal(true);
            }}
          >
            Adicionar
          </AgnesButton>
        }
      />

      <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard
          accent
          title={
            <>
              Total de CNPJs <b>monitorados</b>
            </>
          }
          value={dash?.quantidadeTotalMonitoradas ?? 0}
          caption={`${dash?.percentualTotalMonitoradas ?? 0}% em relação ao total de CNPJs raíz cadastrados`}
        />
        <StatCard
          title={
            <>
              Situação <b>regular</b>
            </>
          }
          value={dash?.quantidadeRegular ?? 0}
          caption={`${dash?.percentualRegular ?? 0}% em relação ao total de CNPJs monitorados`}
        />
        <StatCard
          title={
            <>
              Situação <b>irregular</b>
            </>
          }
          value={dash?.quantidadeIrregular ?? 0}
          caption={`${dash?.percentualIrregular ?? 0}% em relação ao total de CNPJs monitorados`}
        />
      </div>

      <div className="mt-6 rounded-2xl border border-agnes-border bg-agnes-bg-surface p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="min-w-[320px] flex-1">
            <SearchInput
              value={busca}
              onValueChange={setBusca}
              onSearch={() => useCrfStore.getState().buscar()}
              placeholder="Pesquisar por empregador ou código de controle"
            />
          </div>
          <div className="min-w-[240px]">
            <AgnesSelect value={syncCnpj} onValueChange={setSyncCnpj}>
              <AgnesSelectTrigger className={SELECT_TRIGGER}>
                <AgnesSelectValue placeholder="Empregador para sincronizar" />
              </AgnesSelectTrigger>
              <AgnesSelectContent>
                {empregadores.map((e) => (
                  <AgnesSelectItem key={e.cnpjFilial} value={e.cnpjFilial}>
                    {e.company || e.fantasyName || ''}
                  </AgnesSelectItem>
                ))}
              </AgnesSelectContent>
            </AgnesSelect>
          </div>
          <AgnesButton
            variant="primary"
            disabled={!syncCnpj || sincronizando}
            onClick={() => useCrfStore.getState().sincronizar(syncCnpj)}
            startIcon={<RefreshCw className="h-4 w-4" />}
          >
            {sincronizando ? 'Sincronizando...' : 'Sincronizar'}
          </AgnesButton>
          <CrfFiltro />
          <ColumnsMenu
            data-qa="crf-colunas"
            columns={columns}
            hiddenColumnKeys={hiddenColumnKeys}
            onToggleColumn={toggleColumn}
          />
          <ExportButton
            variant="outlined"
            startIcon={<Download className="h-4 w-4" />}
            disabled={exportando}
            onClick={() => useCrfStore.getState().exportar()}
          >
            Exportar
          </ExportButton>
        </div>
        <TableWrapper className="mt-4">
          <AgnesDataTable<Crf>
            columns={columns}
            hiddenColumnKeys={hiddenColumnKeys}
            data={lista}
            loading={carregando}
            rowKey={(r) => String(r.id)}
            sort={{ sortBy, sortOrder, onSortChange: setSort }}
            pagination={{
              page,
              pageSize: size,
              total,
              serverSide: true,
              onPageChange: (p: number) => useCrfStore.getState().setPage(p),
              onPageSizeChange: (s: number) => useCrfStore.getState().setSize(s),
            }}
          />
        </TableWrapper>
      </div>

      <AdicionarCrf
        open={modal}
        onClose={() => {
          setModal(false);
          setEditarLinha(null);
        }}
        editar={editarLinha}
      />
      <AgnesModal
        open={!!histId}
        title="Histórico do CRF"
        maxWidth="lg"
        onClose={() => setHistId(null)}
      >
        <AgnesDataTable<Any>
          columns={[
            { key: 'status', header: 'Situação' },
            { key: 'codigo', header: 'Código' },
            { key: 'dataEmissao', header: 'Emissão' },
            { key: 'dataValidade', header: 'Validade' },
          ]}
          data={historico}
          rowKey={(r: Any) => String(r.id)}
        />
      </AgnesModal>
    </>
  );
}
